import { useNavigate } from "react-router-dom";
import { useApp } from "@/hooks/apps";
import { Chip, type ChipTone } from "@/components/badge/chip";
import { StateBadge } from "@/components/badge/state-badge";
import { RowCard } from "@/components/list/row-card";
import { CopyShortId } from "@/components/prds/copy-short-id";
import { PrLinks } from "@/components/prds/pr-links";
import { ExecStageChip } from "@/components/prds/exec-stage-chip";
import { AgentSessionChip } from "@/components/prds/agent-session-chip";
import TaskConfirmButton from "@/components/task-confirm/confirm-button";
import { CompletionProgress } from "@/components/engagements/completion-progress";
import { RemindMembersButton, useMyCompletion } from "@/components/engagements/my-completion";
import { taskDue } from "@/components/task-confirm/due";
import { ConfirmQueue } from "@/components/task-confirm/queue";
import { Button } from "@/components/ui/button";
import { RC_TASK_TYPES, refreshRcTasks } from "@/constants/rc";
import { INBOX_COUNT_KEY, INBOX_KEY } from "@/hooks/inbox";
import { mutateRequest, removeListItem } from "@/lib/swr";
import { AccessRequestButtons } from "@/screens/Prds/prd-access-request";
import { ROLLOUT_TASK_LABELS, refreshRolloutTasks } from "@/lib/rollout-task";
import { engagementDetailPath } from "@/lib/paths";
import { cn } from "@/lib/utils";
import type { InboxItem, InboxKind, IssueType } from "@/types/domain";
import { groupInbox, type InboxPrdGroup } from "./group-inbox";

const InboxIssueType = ({ type }: { type?: IssueType }) =>
  type === "bug" ? <Chip tone="orange">Bug</Chip> : null;

/** Kind chip: label + tone, shared with the /inbox filter tabs. */
export const INBOX_KINDS: Record<InboxKind, { label: string; tone: ChipTone }> =
  {
    "engagement": { label: "Prds", tone: "orange" },
    rollout: { label: "数据回收", tone: "purple" },
    "rc-task": { label: "RC", tone: "cyan" },
    "access-request": { label: "访问申请", tone: "blue" },
  };

/** Role text under each kind's own vocabulary (see InboxItem.role). */
export const inboxRoleLabel = (item: InboxItem): string => {
  switch (item.kind) {
    case "rollout":
      return (
        ROLLOUT_TASK_LABELS[item.role as keyof typeof ROLLOUT_TASK_LABELS] ??
        item.role
      );
    case "rc-task":
      return (
        RC_TASK_TYPES[item.role as keyof typeof RC_TASK_TYPES]?.label ??
        item.role
      );
    default:
      return item.role;
  }
};

const DueChip = ({ item }: { item: InboxItem }) => {
  if (!item.dueAt) return null;
  const { date } = taskDue({ dueAt: item.dueAt });
  return (
    <Chip tone={item.overdue ? "red" : "neutral"}>
      {item.overdue ? "overdue" : "due"}
      {/* Date only from md up — phones keep the word, the row stays narrow */}
      <span className="hidden md:inline">&nbsp;{date}</span>
    </Chip>
  );
};

// After an inline action, refresh the entity list the same task shows in
// (version / RC page), so an already-visited page doesn't keep the
// unconfirmed row. The rollout / RC helpers include the inbox themselves;
// anything else leaves the queue locally and only the badge count reloads (WED-249).
const refreshAfterAction = (item: InboxItem) => {
  switch (item.kind) {
    case "rollout":
      refreshRolloutTasks();
      break;
    case "rc-task":
      refreshRcTasks(item.rcId ?? undefined);
      break;
    default:
      removeListItem(INBOX_KEY, item.id);
      mutateRequest(INBOX_COUNT_KEY);
  }
};

const CompleteMyWorkButton = ({ id, issueType }: { id: string; issueType?: InboxItem["issueType"] }) => {
  const { setMyStatus, openTodosDialog, isMutating } = useMyCompletion(id, undefined, issueType);
  return <>
    <Button size="sm" variant="outline" disabled={isMutating}
      onClick={(e) => { e.stopPropagation(); setMyStatus("done"); }}>
      {isMutating ? "Saving…" : "Done"}
    </Button>
    {openTodosDialog}
  </>;
};

// PRD access request (WED-276): either outcome takes the row off the queue.
const AccessRequestActions = ({ item }: { item: InboxItem }) => (
  <AccessRequestButtons
    approveUrl={item.action.endpoint}
    rejectUrl={item.action.rejectEndpoint}
    onDone={() => refreshAfterAction(item)}
  />
);

// 所有工作项的 Done 只完成当前用户,不能关闭整个关系。
const InboxAction = ({ item }: { item: InboxItem }) => {
  const { action } = item;
  if (item.kind === "access-request") return <AccessRequestActions item={item} />;
  // 负责人也能直接 Done;催办是额外按钮(WED-192)
  if (item.kind === "engagement") return <div className="flex gap-2">
    {action.label === "Remind" && <RemindMembersButton id={item.id} />}
    <CompleteMyWorkButton id={item.id} issueType={item.issueType} />
  </div>;
  if (action.method === "POST" && action.endpoint) {
    return (
      <TaskConfirmButton
        endpoint={action.endpoint}
        onConfirmed={() => refreshAfterAction(item)}
        label={action.label}
      />
    );
  }
  // Every other navigate-only item (rollout data tasks, every RC task —
  // approvals included) has no inline control: the whole row opens the page
  // where the work and the decision happen (owner 2026-09-08).
  return null;
};

const TodoProgress = ({ counts }: { counts: InboxItem["todoCounts"] }) => {
  if (!counts?.total) return null;
  const label = `Todo：本人已完成 ${counts.completed} 项，共 ${counts.total} 项`;
  return <span
    // Hidden on phones to save room, like the version chip.
    className={cn("hidden shrink-0 text-xs tabular-nums md:inline", counts.completed < counts.total
      ? "text-destructive" : "text-muted-foreground")}
    title={label}
    aria-label={label}
  >
    {counts.completed}/{counts.total}
  </span>;
};

const InboxAppChip = ({ appId }: { appId: string }) => {
  const app = useApp(appId);
  return <Chip className="max-w-40 truncate" title={app?.name ?? appId}>
    {app?.name ?? `App ${appId}`}
  </Chip>;
};

const groupAppIds = (group: InboxPrdGroup): string[] => [
  ...group.engagements.flatMap(item => item.appId ? [item.appId] : []),
  ...group.todos.flatMap(groupAppIds),
];

export const InboxRow = ({ item, asSubItem = false }: {
  item: InboxItem;
  asSubItem?: boolean;
}) => {
  const navigate = useNavigate();
  const kind = INBOX_KINDS[item.kind];
  // App name resolved from the visible list; a version chip alone doesn't
  // say which product it belongs to (owner 2026-09-08).
  const app = useApp(item.appId ?? undefined);
  const engagementOnly = asSubItem && item.kind === "engagement";
  return (
    <RowCard onClick={() => navigate(item.action.link)}>
      {/* Leading completion circle, as on the App Prds list; desktop only. */}
      {item.kind === "engagement" && (
        <div className="hidden md:contents">
          <CompletionProgress engagement={item} />
        </div>
      )}
      <CopyShortId shortId={item.shortId} />
      {/* Rollout rows carry the task type itself as the kind chip (收入数据 /
          版本数据, owner 2026-09-08); the "数据回收" label only names the
          /inbox tab group. */}
      {engagementOnly ? (
        <span className="min-w-0 flex-1 truncate">
          {app?.name ?? (item.appId ? `App ${item.appId}` : "未关联 App")}
        </span>
      ) : item.kind === "rollout" ? (
        <Chip tone={item.role === "data_check" ? "teal" : "purple"}>
          {inboxRoleLabel(item)}
        </Chip>
      ) : item.kind === "engagement" ? (
        <InboxIssueType type={item.issueType} />
      ) : (
        <Chip tone={kind.tone}>{kind.label}</Chip>
      )}
      {item.kind === "rc-task" ? (
        // Task-type chip with the same tone the RC page uses (审批 purple /
        // 配置 blue / 部署 green / 回滚 orange), owner 2026-09-08
        <Chip
          tone={
            RC_TASK_TYPES[item.role as keyof typeof RC_TASK_TYPES]?.tone ??
            "neutral"
          }
        >
          {inboxRoleLabel(item)}
        </Chip>
      ) : item.kind === "engagement" ? (
        // Inbox 只放我的工作，参与身份（owner / member）不再单独标出。
        null
      ) : (
        item.kind !== "rollout" &&
        inboxRoleLabel(item) && (
          <span className="shrink-0 text-xs text-muted-foreground">
            {inboxRoleLabel(item)}
          </span>
        )
      )}
      {/* Rollout rows are about the version itself: the version sits in the
          same App → version → state chip slot as every other row (owner
          2026-09-08), so the title slot stays empty rather than repeating it. */}
      {!engagementOnly && <div className="flex min-w-0 flex-1 items-center gap-1.5">
        <span className="truncate">{item.kind === "rollout" ? "" : item.title}</span>
        <PrLinks prd={item} variant="text" />
      </div>}
      {/* Keep App context visible. Version metadata hides on phones except
          for rollout rows, where the version identifies the work. */}
      {/* Only work rows have an exec stage; rollout / RC rows keep no slot. */}
      {!engagementOnly && item.kind === "engagement" && <AgentSessionChip prd={item} fixed />}
      {!engagementOnly && item.kind === "engagement" && <ExecStageChip prd={item} fixed />}
      {!engagementOnly && item.appId && <InboxAppChip appId={item.appId} />}
      {item.versionId && item.versionName && (
        <Chip
          className={item.kind === "rollout" ? undefined : "hidden md:inline"}
        >
          {item.versionName}
          {item.versionCode && `(${item.versionCode})`}
        </Chip>
      )}
      {item.versionId && item.versionState != null && (
        <StateBadge state={item.versionState} className="hidden md:inline" />
      )}
      {!engagementOnly && <TodoProgress counts={item.todoCounts} />}
      {/* Phones drop the row actions; the row itself opens the page. */}
      <div className="hidden md:contents">
        <InboxAction item={item} />
      </div>
      {/* Due / overdue at the far right, in the slot an inline action would
          take — only navigate-only rows (rollout / RC) carry a due date
          (owner 2026-09-08). */}
      <DueChip item={item} />
    </RowCard>
  );
};

const PrdGroup = ({ group }: { group: InboxPrdGroup }) => {
  const navigate = useNavigate();
  const { prd, engagements, todos } = group;
  const title = prd?.docName ?? "未找到父项";
  const single = engagements.length === 1;
  const engagementId = engagements[0]?.id ?? prd?.engagementId;
  // Only collapse children when their parent detail is reachable and the
  // server supplied progress; orphaned or older items must remain actionable.
  const visibleTodos = engagementId && prd?.todoCounts ? [] : todos;
  return (
    <section className="space-y-1" aria-label={`${prd?.shortId ?? title} 的待办`}>
      {single ? <InboxRow item={engagements[0]} /> : <RowCard onClick={engagementId ? () => navigate(engagementDetailPath(engagementId)) : undefined}>
        {engagements.length === 0 && <CopyShortId shortId={prd?.shortId} />}
        <InboxIssueType type={prd?.type} />
        <div className="flex min-w-0 flex-1 items-center gap-1.5">
          <span className="truncate font-medium">{title}</span>
          {prd && <PrLinks prd={prd} variant="text" />}
        </div>
        {engagements.length === 0 && [...new Set(groupAppIds(group))].map(appId => (
          <InboxAppChip key={appId} appId={appId} />
        ))}
        <ExecStageChip prd={prd ?? {}} fixed />
        <TodoProgress counts={prd?.todoCounts} />
      </RowCard>}
      {(!single || visibleTodos.length > 0) && <div className="ml-4 flex flex-col gap-1 md:ml-6">
        {!single && engagements.map((item) => <InboxRow key={item.id} item={item} asSubItem />)}
        {visibleTodos.map((todo) => <PrdGroup key={todo.key} group={todo} />)}
      </div>}
    </section>
  );
};

// Items arrive server-sorted. Group documents and todo children before limiting
// the number of visible blocks, so previews never split a document's work.
// `empty` lets a filtered view say "No results." instead of the entity form.
export const InboxList = ({
  items,
  isLoading,
  limit,
  empty = "No inbox items.",
}: {
  items: InboxItem[] | undefined;
  isLoading?: boolean;
  limit?: number;
  empty?: string;
}) => {
  const groups = items ? groupInbox(items) : undefined;
  return (
    <ConfirmQueue
      items={limit && groups ? groups.slice(0, limit) : groups}
      isLoading={isLoading}
      empty={empty}
      renderRow={(group) => group.kind === "prd" ? (
        <PrdGroup key={group.key} group={group} />
      ) : <InboxRow key={group.key} item={group.item} />}
    />
  );
};

export default InboxList;
